// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * The strength meter under a new password (sign-up, reset, change password). Same rules as the
 * server, which refuses the two lowest scores (@granite/core passwordScore).
 */
import { passwordScore, type PasswordScore } from "@granite/core";
import { cn } from "@granite/ui";

import { t, type MessageKey } from "../lib/i18n.js";

const labels: Record<PasswordScore, MessageKey> = {
  0: "password.strength.0",
  1: "password.strength.1",
  2: "password.strength.2",
  3: "password.strength.3",
  4: "password.strength.4",
};
const colors: Record<PasswordScore, string> = { 0: "bg-danger", 1: "bg-danger", 2: "bg-warning", 3: "bg-success", 4: "bg-success" };

export function PasswordStrength({ password, personal = [] }: { password: string; personal?: readonly string[] }) {
  if (!password) return null;
  const score = passwordScore(password, personal);
  const filled = Math.max(1, score);
  return (
    <div className="-mt-2 flex items-center gap-2" aria-live="polite">
      <div className="flex flex-1 gap-1" aria-hidden="true">
        {[1, 2, 3, 4].map((i) => (
          <span key={i} className={cn("h-1 flex-1 rounded-full", i <= filled ? colors[score] : "bg-border")} />
        ))}
      </div>
      <span className="min-w-20 text-right text-xs text-fg-muted">
        <span className="sr-only">{t("password.strength.label")} </span>
        {t(labels[score])}
      </span>
    </div>
  );
}
